/**
 * PaymentsSection.tsx — the ONE admin "Payments" section (UX Wave 3b): the
 * merge of the old "Payments pipeline" and "Payments" sidebar items into a
 * single money surface with a Card view ⇄ List view toggle.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 3b (Payments merge).
 *
 * WHY one section: the CTO's Wave-3 IA feedback (§3) — single responsibility.
 * The CARD view is the four-column money board (PaymentsPipelineBoard),
 * enriched with patient names + medication names. The LIST view is the
 * paginated subscriptions table (25/page via the shared ui/Pagination) with
 * filter chips: "Failed payments" surfaces the failed-payments feed + the
 * recent-payments ledger with its ADMIN Refund action (the old Payments tab,
 * folded in whole), and "No subscription yet" surfaces order cases that never
 * grew a subscription (the existing staff orders read — replacing the deleted
 * "All order cases" collapsed section; the deleted "All subscriptions"
 * collapsed table is redundant because the board IS every subscription).
 * Board cards AND list rows open the SAME drill-down Drawer
 * (GET /v1/orders/admin/subscriptions/:id) — one detail surface, never two.
 *
 * Money surface rules: ADMIN-only (R41 — the routes 403 everyone else); every
 * figure is integer pence formatted at the edge; patient identity is composed
 * client-side from the batched identity lookup (ONE call per view, never one
 * per row). NO drag on the board in this slice — a drag must map to a real
 * owning action (cancel-at-term / retry), which needs orders-side endpoints
 * (documented gap; a freeform state overwrite is forbidden).
 *
 * WAVE 6 §3 (payments correctness): cards AND list rows lead with the patient
 * NAME with the EMAIL secondary; the drill-down drawer opens with a FULL
 * patient block (name, email, delivery address, and a "View patient record"
 * deep link into the PMS section — `?tab=patients&patient=<id>`, LIVE since
 * Wave-6 §5 claimed the key); and the notes thread is the PAYMENT one
 * (admin/PaymentNotes.tsx) — clinical notes NEVER render in this drawer (the
 * interim `[payment]` marker in lib/paymentNotes.ts segregates the shared
 * case-notes store until the backend grows kind='payment').
 *
 * WAVE 6 §5 (two-way deep links): `?subscription=<id>` opens the drill-down
 * drawer directly — the PMS patient record's subscription rows link here, the
 * reverse of this drawer's "View patient record" link.
 *
 * TEST RECORDS IN BOTH VIEWS (2026-09-11). Staff-marked test records are hidden
 * by default everywhere on this screen and revealed by ONE control, the Filters
 * trigger on the shared view row. It used to drive the board alone; orders-
 * service now takes the same `includeTest` opt-in on the subscriptions and
 * failed-payments reads, so the List view follows it too. Flagged rows wear the
 * muted Test badge, a test row in the failed-payments feed offers no payment
 * link (the server refuses one), a captured test payment keeps its Refund, and
 * "No subscription yet" says plainly that it cannot list test records.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { lookupStaffContactMap, type StaffContact } from '../api/identity';
import { cancelSubscription, getAdminSubscriptions, getStaffQueue, getSubscriptionDetail, refundPayment } from '../api/orders';
import { getPlanOptions } from '../api/prices';
import type { StaffOrder, StaffPaymentRecord, StaffSubscriptionDetail, StaffSubscriptionItem } from '../api/types';
import { formatAddress, formatMonthly, formatPence, formatUtcDate } from '../lib/format';
import { SectionTitle } from '../staff/ui';
import { PaymentNotesSection } from './PaymentNotes';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { PrescribedItems } from '../ui/PrescribedItems';
import { PrescribedSupplies } from '../ui/PrescribedSupplies';
import { ResubscribeSection } from './ResubscribeSection';
import { Pagination, paginate } from '../ui/Pagination';
import { DataTable, Td, Th } from '../ui/Table';
import { labelFor, toneFor } from '../ui/tones';
import { Alert, Badge, Button, Card, Drawer, ErrorState, Help } from '../ui/kit';
import { DrawerSkeleton, TableSkeleton } from '../ui/skeletons';
import { FailedPayments } from './FailedPayments';
import { PaymentsPipelineBoard } from './PaymentsPipelineBoard';
/* The estate's shared filter grammar — the same FiltersTrigger / FilterChip /
   FilterChipRow the CRM pipeline and the Patients directory draw from, and the
   same CONTROL_ROW_HEIGHT every control on a filter row is sized to.
   Three screens, one grammar (see ui/filters.tsx). */
import { CONTROL_ROW_HEIGHT, FilterChip, FilterChipRow, FiltersTrigger } from '../ui/filters';
import {
  activePaymentsFilterCount,
  EMPTY_PAYMENTS_FILTERS,
  PAYMENTS_TEST_CHIP,
  PaymentsFilters,
  type PaymentsFilterState,
} from './PaymentsFilters';

/** What the shared drawer needs — the board and every list row produce it. */
interface SelectedSubscription {
  subscriptionId: string;
  title: string;
}

type View = 'cards' | 'list';
type ListFilter = 'all' | 'failed' | 'nosub';

export function PaymentsSection({ clinicSlug }: { clinicSlug: string }) {
  const [view, setView] = useState<View>('cards');
  const [selected, setSelected] = useState<SelectedSubscription | null>(null);
  // productKey → medication name, from the SAME plan-options read the patient
  // plan chooser uses (C2.1). Best-effort: on failure the raw productKey renders.
  const [medicationNames, setMedicationNames] = useState<Record<string, string>>({});

  // Wave 6 §5 (two-way deep links): `?subscription=<id>` opens the drill-down
  // drawer directly — the PMS patient record's subscription rows land here,
  // completing the loop with this drawer's own "View patient record" link.
  // The id doubles as the title until the detail loads (the panel is the
  // real content); closing the drawer clears the param so it never re-opens.
  const [searchParams, setSearchParams] = useSearchParams();
  const subscriptionParam = searchParams.get('subscription');
  useEffect(() => {
    if (!subscriptionParam) return;
    setSelected((prev) =>
      prev?.subscriptionId === subscriptionParam ? prev : { subscriptionId: subscriptionParam, title: subscriptionParam },
    );
  }, [subscriptionParam]);

  const closeDrawer = useCallback(() => {
    setSelected(null);
    if (subscriptionParam) {
      const next = new URLSearchParams(searchParams);
      next.delete('subscription');
      setSearchParams(next, { replace: true });
    }
  }, [subscriptionParam, searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    getPlanOptions(clinicSlug)
      .then((data) => {
        if (cancelled) return;
        setMedicationNames(Object.fromEntries(data.options.map((o) => [o.productKey, o.medicationName])));
      })
      .catch(() => {
        // Best-effort — cards/rows fall back to the raw productKey.
      });
    return () => {
      cancelled = true;
    };
  }, [clinicSlug]);

  const openSubscription = useCallback(
    (subscriptionId: string, title: string) => setSelected({ subscriptionId, title }),
    [],
  );

  /* THE SECTION'S FILTER STATE, LIFTED HERE (CTO, 2026-09-06) so the control can
     render on the view row above both views — and since 2026-09-11 it is a
     dependency of the List view's reads as well as the board's. It is
     deliberately NOT reset when the view toggles: flipping to List and back is
     navigation, not a decision to stop looking at test records, and silently
     clearing an applied audit filter would show a money position that is not
     the one the operator set. */
  const [filters, setFilters] = useState<PaymentsFilterState>(EMPTY_PAYMENTS_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const appliedCount = activePaymentsFilterCount(filters);

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
      {/* ── THE VIEW ROW IS ALSO THE FILTER ROW (CTO, 2026-09-06) ───────────
          The toggle is two pills; as a grid item it is full width, so
          everything to its right was empty. The Filters control used to sit on
          its own row underneath, inside PaymentsPipelineBoard — two toolbar
          rows stacked above the lanes to carry four controls' worth of content.
          The pipeline board already solved this shape; this is the same answer.

          ⚠️ CHIPS IN THIS ROW, NEVER ON ONE OF THEIR OWN — the bug already
          fixed on the pipeline board. A full-width chip row appears the moment
          a filter is applied and pushes everything below it down by its own
          height, so the lanes jump away from the operator at the exact moment
          they act. The chips take the same empty space the button does and
          nothing below moves.

          `alignItems: 'flex-start'` because at a narrow viewport the wrapped
          chip block is the one item allowed to be taller than the row's control
          height; `flexWrap` so the trigger drops under the toggle rather than
          squeezing it when there is genuinely no room. `gap` is the pipeline
          bar's, so the three screens space their controls identically. */}
      <div
        data-testid="payments-toolbar"
        style={{ display: 'flex', gap: 'var(--gps-s-3)', alignItems: 'flex-start', flexWrap: 'wrap' }}
      >
        <ViewToggle view={view} onChange={setView} />
        {/* BOTH VIEWS (2026-09-11) — this was ⚠️ CARD VIEW ONLY. The trigger
            used to drive the board's read alone, so over the List view it would
            have opened a panel that changed nothing there. orders-service now
            takes the same `includeTest` opt-in on the subscriptions and
            failed-payments reads, so the one applied state decides what BOTH
            views show and the control belongs on the row they share. The list's
            pills still choose WHICH list; this chooses whether test records are
            in it. */}
        <FiltersTrigger
          appliedCount={appliedCount}
          onOpen={() => setFiltersOpen(true)}
          onClearAll={() => setFilters(EMPTY_PAYMENTS_FILTERS)}
        />
        {appliedCount > 0 ? (
          <FilterChipRow>
            {filters.showTest ? (
              <FilterChip
                label={PAYMENTS_TEST_CHIP}
                onRemove={() => setFilters((f) => ({ ...f, showTest: false }))}
              />
            ) : null}
          </FilterChipRow>
        ) : null}
      </div>

      {view === 'cards' ? (
        <PaymentsPipelineBoard
          medicationNames={medicationNames}
          onOpenSubscription={openSubscription}
          filters={filters}
        />
      ) : (
        <PaymentsListView
          medicationNames={medicationNames}
          onOpenSubscription={openSubscription}
          showTest={filters.showTest}
        />
      )}

      {/* Mounted only while OPEN — DialogShell does its focus-in on mount and
          returns focus to the trigger on close, which is what makes the panel
          keyboard-safe from the "Filters" button. It is mounted by the SECTION
          now, beside the trigger that opens it, so the two cannot be separated
          by a board that failed to load. */}
      {filtersOpen ? (
        <PaymentsFilters
          applied={filters}
          onApply={(next) => {
            setFilters(next);
            setFiltersOpen(false);
          }}
          onClose={() => setFiltersOpen(false)}
        />
      ) : null}

      <Drawer
        open={selected !== null}
        onClose={closeDrawer}
        title={selected ? `Subscription — ${selected.title}` : 'Subscription'}
        width="lg"
      >
        {selected ? <SubscriptionDetailPanel key={selected.subscriptionId} subscriptionId={selected.subscriptionId} /> : null}
      </Drawer>
    </div>
  );
}

/**
 * The pill style shared by the view toggle and the list filter chips.
 *
 * ⚠️ SIZED TO `CONTROL_ROW_HEIGHT`, NOT TO ITSELF (2026-09-06). The view toggle
 * now shares a row with the estate's `FiltersTrigger`, and ui/filters.tsx
 * settles this exact question with the reasoning already applied to the
 * pipeline bar: controls at 36px and 48px read as two unrelated things however
 * they are aligned, because it is the differing HEIGHT and not the alignment
 * that the eye picks up. One height and `alignItems` agree at every edge.
 *
 * The LIST view's filter pills take it too, though nothing shares their row.
 * They sit directly beneath this toggle, so leaving them at 36 would swap one
 * mismatch for another one stacked vertically — and 48 clears the WCAG 2.5.5
 * 44px target floor with room to spare, which 36 did only via padding.
 */
function pillStyle(active: boolean) {
  return {
    minHeight: CONTROL_ROW_HEIGHT,
    height: CONTROL_ROW_HEIGHT,
    padding: '6px 14px',
    borderRadius: 'var(--gps-r-pill)',
    border: `1.5px solid ${active ? 'var(--gps-primary)' : 'var(--gps-border-strong)'}`,
    background: active ? 'var(--gps-primary-light)' : 'var(--gps-surface)',
    color: active ? 'var(--gps-green-text)' : 'var(--gps-muted)',
    font: 'inherit',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  } as const;
}

/** Card view ⇄ List view — a pressed-state toggle pair, not a tab widget. */
function ViewToggle({ view, onChange }: { view: View; onChange: (view: View) => void }) {
  return (
    <div role="group" aria-label="Payments view" style={{ display: 'flex', gap: 'var(--gps-s-2)' }}>
      {(
        [
          { key: 'cards', label: 'Card view' },
          { key: 'list', label: 'List view' },
        ] as const
      ).map(({ key, label }) => (
        <button key={key} type="button" aria-pressed={view === key} onClick={() => onChange(key)} style={pillStyle(view === key)}>
          {label}
        </button>
      ))}
    </div>
  );
}

/* ── List view ─────────────────────────────────────────────────────────── */

const LIST_FILTERS: Array<{ key: ListFilter; label: string }> = [
  { key: 'all', label: 'All subscriptions' },
  { key: 'failed', label: 'Failed payments' },
  { key: 'nosub', label: 'No subscription yet' },
];

function PaymentsListView({
  medicationNames,
  onOpenSubscription,
  showTest,
}: {
  medicationNames: Record<string, string>;
  onOpenSubscription: (subscriptionId: string, title: string) => void;
  /** The section's applied "Show test records" — a dependency of the reads below, never a client-side predicate. */
  showTest: boolean;
}) {
  const [filter, setFilter] = useState<ListFilter>('all');
  const [items, setItems] = useState<StaffSubscriptionItem[] | null>(null);
  const [patientContacts, setPatientContacts] = useState<Record<string, StaffContact>>({});
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  // The subscriptions read backs BOTH the "All subscriptions" table and the
  // "No subscription yet" exclusion set, so the list view owns it once. A change
  // of `showTest` re-asks the server: the flagged records are not in the default
  // response at all, so there is nothing here to filter locally.
  const load = useCallback(() => {
    setStatus('loading');
    getAdminSubscriptions(showTest)
      .then(async (data) => {
        setItems(data.items);
        setStatus('ready');
        // ONE batched lookup call for every visible patientUserId — never one per row.
        const ids = Array.from(
          new Set(data.items.map((i) => i.subscription.patientUserId).filter((id): id is string => Boolean(id))),
        );
        if (ids.length === 0) return;
        try {
          setPatientContacts(await lookupStaffContactMap(ids));
        } catch {
          // Best-effort — rows fall back to the raw patientUserId if the lookup fails.
        }
      })
      .catch(() => setStatus('error'));
  }, [showTest]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
      <div role="group" aria-label="Filter payments" style={{ display: 'flex', gap: 'var(--gps-s-2)', flexWrap: 'wrap' }}>
        {LIST_FILTERS.map(({ key, label }) => (
          <button key={key} type="button" aria-pressed={filter === key} onClick={() => setFilter(key)} style={pillStyle(filter === key)}>
            {label}
          </button>
        ))}
      </div>

      {/* Keyed on the filter so the table starts again at page 1: page two of
          "subscriptions" is not page two of "subscriptions and test records" —
          the same reason the Patients directory resets its cursor on this tick. */}
      {filter === 'all' ? (
        <SubscriptionsTable
          key={showTest ? 'with-test-records' : 'real-only'}
          items={items}
          status={status}
          onRetry={load}
          patientContacts={patientContacts}
          medicationNames={medicationNames}
          onOpenSubscription={onOpenSubscription}
        />
      ) : null}

      {filter === 'failed' ? (
        // The old "Payments" tab, folded in whole: the failed-payments feed
        // FIRST (the alerting purpose), then the recent-payments ledger with
        // the ADMIN-only, reason-gated Refund.
        <>
          <FailedPayments showTest={showTest} />
          <RecentPayments showTest={showTest} />
        </>
      ) : null}

      {filter === 'nosub' ? <CasesWithoutSubscription subscribed={items} showTest={showTest} /> : null}
    </div>
  );
}

/** The paginated subscriptions table — Wave 3 §2: 25 rows per page. */
function SubscriptionsTable({
  items,
  status,
  onRetry,
  patientContacts,
  medicationNames,
  onOpenSubscription,
}: {
  items: StaffSubscriptionItem[] | null;
  status: 'loading' | 'ready' | 'error';
  onRetry: () => void;
  patientContacts: Record<string, StaffContact>;
  medicationNames: Record<string, string>;
  onOpenSubscription: (subscriptionId: string, title: string) => void;
}) {
  const [page, setPage] = useState(1);

  // Wave 6 §1: row-shaped loading — the columns below, as skeleton rows.
  if (status === 'loading') return <TableSkeleton rows={5} columns={6} label="Loading subscriptions…" />;
  if (status === 'error' || !items) {
    return <ErrorState message="Sorry — we could not load the subscriptions." onRetry={onRetry} />;
  }

  const visible = paginate(items, page);

  return (
    <Card>
      <SectionTitle>Subscriptions</SectionTitle>
      {items.length === 0 ? (
        <Help style={{ marginBottom: 0 }}>No subscriptions yet — they appear when a patient checks out.</Help>
      ) : (
        <>
          <DataTable
            label="Subscriptions"
            head={
              <>
                <Th>Patient</Th>
                <Th>Product</Th>
                <Th>Term</Th>
                <Th style={{ textAlign: 'right' }}>Monthly</Th>
                <Th>State</Th>
                <Th>Next payment</Th>
              </>
            }
          >
            {visible.map((item) => {
              const sub = item.subscription;
              // Read off the ROW, `=== true`: an orders build from before the
              // reveal sends no `isTest`, and absent is "not told".
              const isTest = item.isTest === true;
              const plan = `${medicationNames[sub.productKey] ?? sub.productKey} (${sub.months} mo)`;
              const open = () => onOpenSubscription(sub.subscriptionId, plan);
              // Wave 6 §3: NAME headline, EMAIL secondary. No stored name → the
              // email IS the headline (no duplicate line); no contact → raw id.
              const contact = sub.patientUserId ? patientContacts[sub.patientUserId] : undefined;
              const headline = contact ? contact.fullName ?? contact.email : sub.patientUserId ?? '—';
              const secondaryEmail = contact?.fullName ? contact.email : null;
              return (
                <tr key={sub.subscriptionId} onClick={open} style={{ cursor: 'pointer' }}>
                  <Td>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        open();
                      }}
                      style={{
                        minHeight: '44px',
                        padding: '0 4px',
                        background: 'none',
                        border: 0,
                        color: 'var(--gps-link)',
                        font: 'inherit',
                        fontSize: '14px',
                        fontWeight: 600,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      {/* STRUCK THROUGH ON A TEST RECORD — the board card's
                          treatment. Decoration on top of the Test badge beside
                          it, never the only cue (WCAG 1.4.1). */}
                      <span style={isTest ? { textDecoration: 'line-through' } : undefined}>{headline}</span>
                    </button>
                    {/* The MUTED chip, verbatim from the board card: a desk tells
                        a rehearsal from real money at a glance without it
                        shouting over the state chip in the same row. */}
                    {isTest ? (
                      <span style={{ marginLeft: '4px', verticalAlign: 'middle' }}>
                        <Badge tone="muted">Test</Badge>
                      </span>
                    ) : null}
                    {secondaryEmail ? (
                      <span style={{ display: 'block', padding: '0 4px', fontSize: '12.5px', color: 'var(--gps-muted)', overflowWrap: 'anywhere' }}>
                        {secondaryEmail}
                      </span>
                    ) : null}
                  </Td>
                  <Td>{medicationNames[sub.productKey] ?? sub.productKey}</Td>
                  <Td>{sub.months} mo</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {formatMonthly(sub.monthlyPence)}
                  </Td>
                  <Td>
                    <Badge tone={toneFor(sub.state)}>{labelFor(sub.state)}</Badge>
                  </Td>
                  <Td>{formatUtcDate(sub.nextPaymentDate) || '—'}</Td>
                </tr>
              );
            })}
          </DataTable>
          <div style={{ marginTop: 'var(--gps-s-3)' }}>
            <Pagination label="Subscriptions pages" page={page} total={items.length} onPageChange={setPage} />
          </div>
        </>
      )}
    </Card>
  );
}

/**
 * Order cases that never grew a subscription — the "No subscription yet" list
 * filter (Wave 3b replaces the deleted "All order cases" section with this
 * single-purpose slice of the existing staff orders read).
 */
function CasesWithoutSubscription({
  subscribed,
  showTest,
}: {
  subscribed: StaffSubscriptionItem[] | null;
  showTest: boolean;
}) {
  const [orders, setOrders] = useState<StaffOrder[] | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [page, setPage] = useState(1);

  const load = useCallback(() => {
    setStatus('loading');
    getStaffQueue()
      .then((data) => {
        setOrders(data);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Exclude any case a subscription already references — what remains is the
  // "checked out but never converted to a plan" set the admin chases.
  const withoutSubscription = useMemo(() => {
    const subscribedCaseIds = new Set((subscribed ?? []).map((i) => i.subscription.orderCaseId));
    return (orders ?? []).filter((o) => !subscribedCaseIds.has(o.orderCaseId));
  }, [orders, subscribed]);

  if (status === 'loading') return <TableSkeleton rows={5} columns={5} label="Loading order cases…" />;
  if (status === 'error' || !orders) {
    return <ErrorState message="Sorry — we could not load the order cases." onRetry={load} />;
  }

  const visible = paginate(withoutSubscription, page);

  return (
    <Card>
      <SectionTitle>Cases without a subscription</SectionTitle>
      {/* THE ONE LIST THE FILTER CANNOT REACH (2026-09-11), said while it is on.
          This is the staff order queue, whose rows carry no test mark, so a
          revealed test case here could not be told from a real one — the read
          stays as it was, real cases only. Without this line an admin auditing
          test records would take the list's silence to mean there are none. */}
      {showTest ? (
        <Help style={{ margin: '0 0 12px' }}>
          Test records are not listed here. This list comes from the order queue, which cannot mark which cases
          are tests, so it only shows real ones.
        </Help>
      ) : null}
      {withoutSubscription.length === 0 ? (
        <Help style={{ marginBottom: 0 }}>No cases waiting — every order case has a subscription.</Help>
      ) : (
        <>
          <DataTable
            label="Cases without a subscription"
            head={
              <>
                <Th>Journey</Th>
                <Th>Status</Th>
                <Th style={{ textAlign: 'right' }}>Amount</Th>
                <Th>Reference</Th>
                <Th>Date</Th>
              </>
            }
          >
            {visible.map((order) => (
              <tr key={order.orderCaseId}>
                <Td>{order.lane === 'form' ? 'Form journey' : order.lane === 'callback' ? 'Callback journey' : order.lane}</Td>
                <Td>
                  <Badge tone={toneFor(order.state)}>{order.state}</Badge>
                </Td>
                <Td style={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {formatMonthly(order.monthlyPence ?? 0)}
                </Td>
                <Td>
                  {order.orderCaseId}
                  {order.months ? ` · ${order.months} month${order.months === 1 ? '' : 's'}` : ''}
                </Td>
                <Td>{formatUtcDate(order.createdAt)}</Td>
              </tr>
            ))}
          </DataTable>
          <div style={{ marginTop: 'var(--gps-s-3)' }}>
            <Pagination label="Order case pages" page={page} total={withoutSubscription.length} onPageChange={setPage} />
          </div>
        </>
      )}
    </Card>
  );
}

/* ── Recent payments + refund (folded from the old Payments tab) ─────────── */

/** The most recent payments to show — a working ledger view, not an archive. */
const RECENT_PAYMENTS_CAP = 50;

/**
 * Flatten every subscription's payment ledger into one newest-first list,
 * capped. Exported for a focused unit test — the ordering/cap is the one bit
 * of real logic in the ledger.
 */
export function flattenRecentPayments(
  items: Array<{ payments: StaffPaymentRecord[] }>,
  cap = RECENT_PAYMENTS_CAP,
): StaffPaymentRecord[] {
  return items
    .flatMap((item) => item.payments)
    .sort((a, b) => (a.occurredAt < b.occurredAt ? 1 : a.occurredAt > b.occurredAt ? -1 : 0))
    .slice(0, cap);
}

/**
 * The order cases the server flagged as test records, for badging the flattened
 * ledger. The flag rides the subscription ITEM, so it is read there before the
 * payments are flattened away from it; `=== true` because absent is "not told".
 */
function testOrderCaseIds(items: StaffSubscriptionItem[]): ReadonlySet<string> {
  return new Set(
    items
      .filter((item) => item.isTest === true)
      .flatMap((item) => [item.subscription.orderCaseId, ...item.payments.map((p) => p.orderCaseId)]),
  );
}

/** Compact chrome for the in-table Refund button. */
const rowButtonStyle = { width: 'auto', minHeight: '36px', padding: '6px 14px', fontSize: '13px' } as const;

function RecentPayments({ showTest }: { showTest: boolean }) {
  const [payments, setPayments] = useState<StaffPaymentRecord[] | null>(null);
  const [testOrderIds, setTestOrderIds] = useState<ReadonlySet<string>>(() => new Set());
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  // The payment whose refund dialog is open (null = closed), plus the async
  // state of the refund POST and a one-off error banner.
  const [refundTarget, setRefundTarget] = useState<StaffPaymentRecord | null>(null);
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);

  const load = useCallback(() => {
    setStatus('loading');
    // Same read, same rule as the table: the filter is a dependency of the
    // request, because a test record's payments are not in the default ledger.
    getAdminSubscriptions(showTest)
      .then((data) => {
        setPayments(flattenRecentPayments(data.items));
        setTestOrderIds(testOrderCaseIds(data.items));
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, [showTest]);

  useEffect(() => {
    load();
  }, [load]);

  // The reason is REQUIRED by the dialog (requireReason gates confirm behind ≥3
  // chars), so `reason` is always present here; guard defensively anyway.
  function confirmRefund(reason?: string) {
    if (!refundTarget || !reason) return;
    setRefundBusy(true);
    setRefundError(null);
    refundPayment(refundTarget.paymentRecordId, reason)
      .then(() => {
        setRefundBusy(false);
        setRefundTarget(null);
        load(); // re-fetch so the row now reads 'refunded'
      })
      .catch((error: unknown) => {
        setRefundBusy(false);
        // Close the dialog FIRST — the error banner renders inside the Card,
        // which sits BEHIND the ConfirmDialog's fixed full-screen overlay, so
        // leaving the dialog open would hide the only failure feedback. The
        // row's Refund button remains as the retry.
        setRefundTarget(null);
        // 409 = the server's refund atomic-claim: another admin refunded this
        // payment concurrently (held/released → 400, already-refunded → 409).
        const alreadyRefunded = error instanceof ApiError && error.status === 409;
        setRefundError(
          alreadyRefunded
            ? 'This payment was already refunded (another admin got there first) — the ledger below has been refreshed.'
            : 'Sorry — the refund could not be completed. Please try again.',
        );
        // Reload either way: on a 409 the row comes back as "refunded" instead
        // of offering Refund again; otherwise it simply reflects server truth.
        load();
      });
  }

  return (
    <Card>
      <SectionTitle>Recent payments</SectionTitle>
      {refundError ? (
        <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
          {refundError}
        </Alert>
      ) : null}
      {status === 'loading' ? (
        <TableSkeleton rows={5} columns={6} label="Loading recent payments…" />
      ) : status === 'error' || !payments ? (
        <ErrorState message="Sorry — we could not load the recent payments." onRetry={load} />
      ) : payments.length === 0 ? (
        <Help style={{ marginBottom: 0 }}>No payments recorded yet — they appear as holds and captures happen.</Help>
      ) : (
        <DataTable
          label="Recent payments"
          head={
            <>
              <Th>Payment</Th>
              <Th>Order</Th>
              <Th style={{ textAlign: 'right' }}>Amount</Th>
              <Th>Phase</Th>
              <Th>Date</Th>
              <Th>Action</Th>
            </>
          }
        >
          {payments.map((payment) => (
            <tr key={payment.paymentRecordId}>
              <Td>{payment.paymentRecordId}</Td>
              <Td>
                <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                  Order {payment.orderCaseId}
                  {testOrderIds.has(payment.orderCaseId) ? <Badge tone="muted">Test</Badge> : null}
                </span>
              </Td>
              <Td style={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {formatPence(payment.amountPence)}
              </Td>
              <Td>
                <Badge tone={toneFor(payment.phase)}>{labelFor(payment.phase)}</Badge>
              </Td>
              <Td>{formatUtcDate(payment.occurredAt)}</Td>
              <Td>
                {/* Only a CAPTURED payment can be refunded — the server enforces
                    this too (held/released → 400, already-refunded → 409).
                    ⚠️ A TEST record's captured payment KEEPS its Refund. A
                    rehearsal that was really charged took real money off a real
                    card, and refunding it is exactly the remedy the CTO asked
                    for; the Test badge says what it is. Hiding the button here
                    would strand the money. */}
                {payment.phase === 'captured' ? (
                  <Button
                    variant="ghost"
                    aria-label={`Refund payment ${payment.paymentRecordId}`}
                    onClick={() => {
                      setRefundError(null);
                      setRefundTarget(payment);
                    }}
                    style={rowButtonStyle}
                  >
                    Refund
                  </Button>
                ) : null}
              </Td>
            </tr>
          ))}
        </DataTable>
      )}

      <ConfirmDialog
        open={refundTarget !== null}
        title="Refund this payment?"
        tone="danger"
        confirmLabel="Refund"
        requireReason
        reasonLabel="Reason for refund"
        busy={refundBusy}
        body={
          refundTarget ? (
            <>
              You’re about to refund <strong>{formatPence(refundTarget.amountPence)}</strong> on payment{' '}
              {refundTarget.paymentRecordId}. This reverses the charge at the payment provider and is recorded in the
              audit log.
            </>
          ) : null
        }
        onConfirm={confirmRefund}
        onCancel={() => {
          if (!refundBusy) setRefundTarget(null);
        }}
      />
    </Card>
  );
}

/* ── The shared drill-down drawer ─────────────────────────────────────────── */

/** The subscription drill-down: patient + order + payments + prescriptions + pharmacy sends. */
function SubscriptionDetailPanel({ subscriptionId }: { subscriptionId: string }) {
  const [detail, setDetail] = useState<StaffSubscriptionDetail | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  // The full patient block's contact (Wave 6 §3) — best-effort: on lookup
  // failure the block falls back to the raw patientUserId.
  const [contact, setContact] = useState<StaffContact | null>(null);
  // Admin immediate-cancel (stops future billing from the next cycle; no
  // refund). Confirm-gated, like the Refund action — the API enforces it.
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const load = useCallback(() => {
    setStatus('loading');
    getSubscriptionDetail(subscriptionId)
      .then(async (d) => {
        setDetail(d);
        setStatus('ready');
        const patientUserId = d.subscription.patientUserId;
        if (!patientUserId) return;
        try {
          setContact((await lookupStaffContactMap([patientUserId]))[patientUserId] ?? null);
        } catch {
          // Best-effort — the patient block falls back to the raw id.
        }
      })
      .catch(() => setStatus('error'));
  }, [subscriptionId]);

  useEffect(() => {
    load();
  }, [load]);

  function confirmCancelNow() {
    setCancelBusy(true);
    setCancelError(null);
    cancelSubscription(subscriptionId)
      .then(() => {
        setCancelBusy(false);
        setConfirmCancel(false);
        load(); // refetch → the panel now shows `cancelled` and no next payment
      })
      .catch(() => {
        setCancelBusy(false);
        setConfirmCancel(false);
        setCancelError('Sorry — the cancellation could not be completed. Please try again.');
      });
  }

  // Wave 6 §1: drawer-shaped loading — one block per coming section
  // (patient / order / payments / prescriptions), so the panel fills in place.
  if (status === 'loading') return <DrawerSkeleton blocks={4} label="Loading subscription details…" />;
  if (status === 'error' || !detail) return <ErrorState message="Sorry — we could not load this subscription." onRetry={load} />;

  const { subscription, order, payments, prescriptions, pharmacySends } = detail;

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
      {/* Wave 6 §3: the FULL patient block leads the drawer — who this money
          belongs to, before what happened to it. */}
      <div>
        <SectionTitle>Patient</SectionTitle>
        <p style={{ margin: '0 0 2px', fontSize: '15px', fontWeight: 600, color: 'var(--gps-text)' }}>
          {contact ? contact.fullName ?? contact.email : subscription.patientUserId ?? 'No patient on file'}
        </p>
        {contact?.fullName ? (
          <p style={{ margin: '0 0 2px', fontSize: '13.5px', color: 'var(--gps-muted)', overflowWrap: 'anywhere' }}>{contact.email}</p>
        ) : null}
        <p style={{ margin: '0 0 8px', fontSize: '14.5px', color: 'var(--gps-text)' }}>
          {order?.deliveryAddress ? formatAddress(order.deliveryAddress) : 'No delivery address on file.'}
        </p>
        {subscription.patientUserId ? (
          // Deep link into the PMS patient record (Wave-6 §5's coming section).
          // `?tab=patients` is the future-proof key — until that slice lands,
          // AdminPage falls back to the console's default section (never a 404
          // or a blank pane). NOT pointed at `?tab=users`: that is the STAFF
          // directory, where a patient can never appear.
          <Link
            to={`/admin?tab=patients&patient=${encodeURIComponent(subscription.patientUserId)}`}
            style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--gps-link)' }}
          >
            View patient record
          </Link>
        ) : null}
      </div>

      <div>
        <SectionTitle>Order</SectionTitle>
        {order ? (
          <p style={{ margin: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
            <Badge tone={toneFor(order.state)}>{order.state}</Badge>
            {/* Repeat vs new business — the board question. Server-derived from
                the successor plan's link back, never guessed here, and shown
                only when the server actually said so. */}
            {order.isRepeat ? <Badge tone="muted">Repeat term</Badge> : null}
          </p>
        ) : (
          <Help style={{ marginBottom: 0 }}>No linked order case.</Help>
        )}
      </div>

      <div>
        <SectionTitle>Subscription</SectionTitle>
        <p style={{ margin: '0 0 8px' }}>
          <Badge tone={toneFor(subscription.state)}>{subscription.state}</Badge>
          {subscription.nextPaymentDate ? (
            <span style={{ marginLeft: 8, fontSize: '13.5px', color: 'var(--gps-muted)' }}>
              Next payment {formatUtcDate(subscription.nextPaymentDate)}
            </span>
          ) : null}
        </p>
        {cancelError ? (
          <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
            {cancelError}
          </Alert>
        ) : null}
        {/* Immediate cancel is offered only while the plan is still billing.
            A cancelled/completed/dormant plan has nothing to stop. */}
        {subscription.state === 'active' || subscription.state === 'past_due' ? (
          <Button type="button" variant="ghost" onClick={() => setConfirmCancel(true)}>
            Cancel subscription
          </Button>
        ) : (
          <Help style={{ marginBottom: 0 }}>This subscription is no longer billing.</Help>
        )}
      </div>

      {/* End-of-term re-subscribe. Renders nothing at all unless the server sent
          an eligibility answer — a plan that is still billing, or an orders
          revision predating the lane, leaves this drawer exactly as it was. */}
      <ResubscribeSection subscription={subscription} eligibility={detail.resubscribe} />

      <ConfirmDialog
        open={confirmCancel}
        title="Cancel this subscription?"
        tone="danger"
        confirmLabel="Cancel subscription"
        busy={cancelBusy}
        body={
          <>
            This stops all future charges from the next billing cycle. The current paid period is{' '}
            <strong>not refunded</strong>, and this can’t be undone.
          </>
        }
        onConfirm={confirmCancelNow}
        onCancel={() => {
          if (!cancelBusy) setConfirmCancel(false);
        }}
      />

      {/* Pharmacy fulfilment (2026-07-29) — ADMIN visibility of what happened
          AFTER the prescription left us. Rendered only when the case actually
          reached the pharmacy, so an ordinary pre-fulfilment case is unchanged. */}
      {order ? <FulfilmentBlock order={order} /> : null}

      <div>
        <SectionTitle>Payments</SectionTitle>
        {payments.length === 0 ? (
          <Help style={{ marginBottom: 0 }}>No payment history yet.</Help>
        ) : (
          <ul aria-label="Payment history" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '8px' }}>
            {payments.map((payment) => (
              <li
                key={payment.paymentRecordId}
                style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--gps-s-3)', fontSize: '13.5px' }}
              >
                <Badge tone={toneFor(payment.phase)}>{labelFor(payment.phase)}</Badge>
                <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--gps-text)' }}>{formatPence(payment.amountPence)}</span>
                <span style={{ color: 'var(--gps-muted)' }}>{formatUtcDate(payment.occurredAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <SectionTitle>Prescriptions</SectionTitle>
        {prescriptions.length === 0 ? (
          <Help style={{ marginBottom: 0 }}>No prescriptions issued yet.</Help>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '10px' }}>
            {prescriptions.map((rx) => (
              <li key={rx.prescriptionId} style={{ fontSize: '13.5px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '2px' }}>
                  <span style={{ fontWeight: 600, color: 'var(--gps-text)' }}>{formatUtcDate(rx.issuedAt)}</span>
                  {rx.repeatFlag ? <Badge tone="muted">Repeat</Badge> : null}
                </div>
                {/* FULL dispensing detail. The admin used to see STRICTLY LESS
                    of a prescription than the clinician did — this block dropped
                    the directions entirely and printed a bare name × quantity —
                    so two staff surfaces gave two different accounts of one
                    prescription. Both now render through the same component, and
                    an unrecorded field is flagged rather than omitted. */}
                <PrescribedItems items={rx.items} />
                {/* The supplies dispensed with it — the same shared block the
                    clinician's history renders, so the two staff surfaces
                    describe one prescription's supplies identically. */}
                <PrescribedSupplies supplies={rx.supplies} />
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <SectionTitle>Pharmacy sends</SectionTitle>
        {pharmacySends.length === 0 ? (
          <Help style={{ marginBottom: 0 }}>Not sent yet.</Help>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '6px' }}>
            {pharmacySends.map((send) => (
              <li key={send.pharmacySendId} style={{ fontSize: '13.5px', color: 'var(--gps-text)' }}>
                {send.sentAt ? `Sent to Pharmacy Express ${formatUtcDate(send.sentAt)}` : 'Not sent yet'}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Wave 6 §3 CRITICAL FIX: the PAYMENT notes thread — this drawer used
          to render the CLINICAL thread (CaseNotesSection), the wrong surface
          in a payments context. Clinical notes never render here in any form,
          and the payment composer never offers patientVisible (see
          admin/PaymentNotes.tsx). Only shown when the subscription resolves
          to an order case (the notes are case-scoped). */}
      {order ? <PaymentNotesSection orderCaseId={order.orderCaseId} /> : null}
    </div>
  );
}

/**
 * Pharmacy fulfilment — what happened to the order AFTER the prescription left
 * us (pharmacy dispensing lane, 2026-07-29).
 *
 * WHY admin needs it: until this lane existed we sent a prescription to Pharmacy
 * Express and had no idea whether it was dispensed, refused or shipped — which
 * is why nobody could tell a patient their medication was on its way, and why a
 * refused order could sit unnoticed indefinitely.
 *
 * THE REJECTED FLAG is the part that earns its place. A pharmacy refusing to
 * dispense is an exception only ADMIN can resolve (the patient is never told,
 * by design), so it reads as a danger badge with the coarse reason beside it —
 * not a quiet field someone has to go looking for. It is deliberately NOT the
 * same state as a clinician's `rejected`: one is a clinical decision, the other
 * is a fulfilment failure, and collapsing them would leave this drawer unable
 * to say which happened.
 *
 * Every field is optional on `StaffOrder`: a case that never reached the
 * pharmacy renders nothing at all, so existing subscriptions are untouched.
 */
function FulfilmentBlock({ order }: { order: StaffOrder }) {
  const rejected = order.state === 'dispense_rejected' || Boolean(order.dispenseRejectedReason);
  const hasAnything =
    rejected || Boolean(order.dispensedAt) || Boolean(order.trackingNumber) || Boolean(order.dispatchedAt);
  // Nothing to say yet — stay silent rather than render an empty section.
  if (!hasAnything) return null;

  return (
    <div>
      <SectionTitle>Pharmacy fulfilment</SectionTitle>
      {rejected ? (
        <p style={{ margin: '0 0 8px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
          <Badge tone="danger">Pharmacy rejected</Badge>
          <span style={{ fontSize: '13.5px', color: 'var(--gps-text)' }}>
            {order.dispenseRejectedReason ? labelFor(order.dispenseRejectedReason) : 'Reason not recorded'}
          </span>
          {order.dispenseRejectedAt ? (
            <span style={{ fontSize: '13.5px', color: 'var(--gps-muted)' }}>{formatUtcDate(order.dispenseRejectedAt)}</span>
          ) : null}
        </p>
      ) : null}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '6px', fontSize: '13.5px' }}>
        {order.dispensedAt ? <li>Dispensed {formatUtcDate(order.dispensedAt)}</li> : null}
        {order.trackingNumber ? (
          <li>
            Tracking {order.courier ? `${order.courier} · ` : ''}
            <span style={{ fontWeight: 600, color: 'var(--gps-text)' }}>{order.trackingNumber}</span>
            {order.dispatchedAt ? ` · dispatched ${formatUtcDate(order.dispatchedAt)}` : ''}
          </li>
        ) : null}
      </ul>
    </div>
  );
}
